import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import {
  distributeRemainder,
  sum,
  getRemainder,
  parseAmount
} from '../core/util.ts';
import { ALLOCATION_QUERY } from '../flows/shared/services/allocation-service';
import {
  checkSummitIClassEligibility,
  minimumSummitIClassInvestAmount
} from '../flows/shared/summit.utils';
import { addCommas } from '../flows/shared/utils';

export const addRemainder = (funds, totalAmount) => {
  const remainder = getRemainder(totalAmount, sum(funds, 'amount'));

  if (remainder && funds && funds[0]?.amount) {
    /* eslint-disable no-param-reassign */
    funds[0].amount = parseAmount(remainder + parseFloat(funds[0].amount));
    /* eslint-enable no-param-reassign */
  }
  return funds;
};

export const autoSplitPercents = (funds, totalAmount) => {
  const percent = 100 / funds.length;
  const percents = new Array(funds.length).fill(percent);
  const distributedPercents = distributeRemainder(percents, 100);
  const newFunds = funds.map((f, i) => ({
    ...f,
    percent: distributedPercents[i],
    amount: parseAmount((distributedPercents[i] * totalAmount) / 100)
  }));

  return addRemainder(newFunds, totalAmount);
};

export default ({ initialFunds, initialTotalAmount, initialPercentMode }) => {
  const [totalAmount, setTotalAmount] = useState(initialTotalAmount);
  const [percentMode, setPercentMode] = useState(initialPercentMode);
  const [selectedFunds, setSelectedFunds] = useState(initialFunds);

  useEffect(() => {
    setSelectedFunds(
      initialFunds.map(f => ({
        ...f,
        amount: f.amount || parseFloat((f.percent / 100) * initialTotalAmount)
      }))
    );
    setTotalAmount(initialTotalAmount);
  }, [initialFunds, initialTotalAmount]);

  // query allocation data for selectedFunds
  const tickers = selectedFunds.map(f => f.ticker);
  const queryOptions = {
    skip: !tickers.length
  };
  queryOptions.variables = { tickers };
  const { data = {} } = useQuery(ALLOCATION_QUERY, queryOptions);

  const adjustFundsByPercent = (funds, amount) => {
    // split funds by percent whenever total
    // investment amount is changed
    const fundsSplitByPercent = autoSplitPercents(funds, amount);
    return [...fundsSplitByPercent].map(f => ({
      ...f,
      ...(f.percent && { percent: f.percent }),
      amount: f.percent ? parseAmount((f.percent * amount) / 100) : f.amount
    }));
  };

  const areEqualSums = (amountFloat, amountString) => {
    return amountFloat === parseFloat(amountString);
  };

  const percentSum =
    (parseFloat(sum(selectedFunds, 'amount').toFixed(2)) / totalAmount) * 100;
  const amountSum = parseFloat(sum(selectedFunds, 'amount').toFixed(2));
  const validPercentSum = percentSum === 100;
  const validAmountSum = areEqualSums(amountSum, totalAmount);
  const isValidTotal = validPercentSum && validAmountSum;
  const requiredTotalText = percentMode ? '100%' : `$${totalAmount}`;

  const fundAdded = ticker =>
    Boolean(selectedFunds.find(f => f.ticker === ticker || f.name === ticker));

  const addFund = newFund => {
    if (Array.isArray(newFund)) {
      const newFunds = [];
      newFund.forEach(f => {
        return (
          !selectedFunds.find(existing => existing.ticker === f.ticker) &&
          newFunds.push(f)
        );
      });
      const nextFunds = [...selectedFunds, ...newFunds];
      const splitPercents = autoSplitPercents(nextFunds, totalAmount);
      return setSelectedFunds(splitPercents);
    }

    return (
      !selectedFunds.find(existing => existing.ticker === newFund.ticker) &&
      setSelectedFunds(
        autoSplitPercents([...selectedFunds, newFund], totalAmount)
      )
    );
  };

  const removeFund = removeMe => {
    const nextFunds = [...selectedFunds].filter(
      existing => existing.ticker !== removeMe
    );

    return setSelectedFunds(autoSplitPercents(nextFunds, totalAmount));
  };

  const changeTotalAmount = amount => {
    setTotalAmount(amount);
    setSelectedFunds(
      addRemainder(adjustFundsByPercent(selectedFunds, amount), amount)
    );
  };

  const changeValue = (ticker, inputValue) => {
    const value = inputValue;
    const newFunds = [...selectedFunds].map(f =>
      f.ticker !== ticker
        ? f
        : {
            ...f,
            percent: percentMode
              ? parseFloat(parseFloat(value).toFixed(2))
              : parseAmount((value / totalAmount) * 100),
            amount: percentMode
              ? parseAmount((value / 100) * totalAmount)
              : parseFloat(value)
          }
    );
    setSelectedFunds(newFunds);
  };

  const changePortfolioDollarValue = (name, inputValue) => {
    const value = inputValue || 0;
    return setSelectedFunds(
      [...selectedFunds].map(f =>
        f.name !== name
          ? f
          : {
              ...f,
              amount: parseAmount(value)
            }
      )
    );
  };

  const determineFundMinimum = (fund, rewardsAvailable, amount) => {
    if (fund.iClass && checkSummitIClassEligibility(amount, rewardsAvailable)) {
      return addCommas(minimumSummitIClassInvestAmount);
    }

    return addCommas(fund?.minimumInvestmentAmount);
  };

  return {
    percentSum,
    amountSum,
    totalAmount,
    requiredTotalText,
    isValidTotal,
    selectedFunds,
    percentMode,
    data,
    api: {
      addFund,
      removeFund,
      changeValue,
      changePortfolioDollarValue,
      setPercentMode,
      changeTotalAmount,
      fundAdded,
      determineFundMinimum
    }
  };
};
